import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { usePageMeta } from "@/hooks/usePageMeta";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://cashmyproperty.ai/" },
    { "@type": "ListItem", position: 2, name: "Services", item: "https://cashmyproperty.ai/services" },
    { "@type": "ListItem", position: 3, name: "Digital Marketing", item: "https://cashmyproperty.ai/services/digital-marketing" },
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How is your digital marketing different from listing on property portals?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Property portals are passive — you list and hope someone finds you. Our approach is active and targeted. We use intent-based audience segmentation, Google Ads, Meta campaigns, and programmatic display to reach buyers who are actively searching for land in your area. We do not wait for buyers to find you — we put your property in front of the right buyers at the right moment.",
      },
    },
    {
      "@type": "Question",
      name: "What is the typical cost and ROI of a property marketing campaign?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Campaign budgets typically range from ₹25,000 to ₹1,50,000 per month depending on property value and target audience size. Our average client achieves 8–12 qualified buyer inquiries per ₹25,000 spent, with a cost-per-qualified-lead of ₹2,000–3,000. For a property worth ₹1–5 Cr, this represents a marketing cost of under 1% of transaction value — with dramatically faster closure timelines.",
      },
    },
    {
      "@type": "Question",
      name: "Do I need to provide photographs and videos?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "No. We handle all content creation. Our team conducts a professional property shoot — drone aerials, 360-degree walkthroughs, and high-resolution stills. We also create 3D renderings of development potential to help buyers visualize what is possible on your land. All creative assets are included in the campaign setup.",
      },
    },
    {
      "@type": "Question",
      name: "How do I know the campaign is working?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You receive a live dashboard with real-time metrics: impressions, clicks, landing page visits, inquiry form submissions, call tracking, and cost-per-lead. We also provide a monthly performance report with optimization insights. Every inquiry is qualified by our team before being forwarded to you — so you only speak to serious buyers.",
      },
    },
  ],
};

const steps = [
  {
    step: "01",
    title: "Property Audit & Positioning",
    desc: "We audit your property — location, features, pricing, and target buyer profile. Based on the audit, we craft a positioning strategy: what makes your land irresistible and who exactly should see it. This shapes every creative asset that follows.",
    icon: "ri-search-eye-line",
  },
  {
    step: "02",
    title: "Creative Production & Landing Page",
    desc: "Professional photo and video shoot — drone aerials, 360-degree walkthroughs, and development potential 3D renders. We build a dedicated landing page optimized for conversion, with inquiry forms, WhatsApp click-to-chat, and direct call buttons.",
    icon: "ri-camera-lens-line",
  },
  {
    step: "03",
    title: "Campaign Setup & Audience Targeting",
    desc: "Google Ads (Search + Display + YouTube), Meta (Facebook + Instagram), and programmatic display campaigns configured with intent-based audience segments — HNIs, NRIs, investors, developers, and end-users actively searching for property in your area.",
    icon: "ri-crosshair-line",
  },
  {
    step: "04",
    title: "Launch, Monitor & Optimize",
    desc: "Campaigns go live with continuous performance monitoring. AI-driven bid optimization, A/B testing of creatives, and audience refinement based on conversion data. Budget shifts automatically toward the highest-performing channels and audiences.",
    icon: "ri-rocket-line",
  },
  {
    step: "05",
    title: "Lead Qualification & Handover",
    desc: "Every inquiry is qualified by our team — budget verification, intent assessment, and timeline confirmation. Only serious, transaction-ready leads are forwarded to you with a summary brief. You never waste time on casual browsers.",
    icon: "ri-user-star-line",
  },
];

const channels = [
  { icon: "ri-google-line", title: "Google Ads", desc: "Search ads for 'buy land in Bangalore' intent queries. Display and YouTube remarketing to stay top-of-mind. Performance Max campaigns for maximum reach.", reach: "High-Intent Buyers" },
  { icon: "ri-facebook-circle-line", title: "Meta (Facebook & Instagram)", desc: "Interest-based and lookalike audience targeting. Carousel ads showcasing property highlights. Lead form ads for frictionless inquiry capture.", reach: "HNIs & NRIs" },
  { icon: "ri-smartphone-line", title: "WhatsApp Marketing", desc: "Click-to-WhatsApp ads with automated response flows. Broadcast lists for buyer database. Instant sharing of property brochures and virtual tours.", reach: "Direct Engagement" },
  { icon: "ri-linkedin-box-line", title: "LinkedIn Ads", desc: "Targeting real estate investors, developers, and corporate decision-makers by industry, seniority, and company size. Sponsored InMail for direct outreach.", reach: "Institutional Buyers" },
  { icon: "ri-youtube-line", title: "YouTube Video Ads", desc: "Skippable in-stream ads with drone footage and property walkthroughs. 15- and 30-second formats optimized for view-through and click-through.", reach: "Visual Storytelling" },
  { icon: "ri-mail-line", title: "Email & WhatsApp Automation", desc: "Drip campaigns to our 15,000+ verified buyer database. Segmented by budget, location preference, and property type. Automated follow-up sequences.", reach: "Existing Buyer Pool" },
];

const benefits = [
  { icon: "ri-focus-3-line", title: "Intent-Based Targeting", desc: "We target buyers actively searching for property — not random audiences. Keyword intent, browsing behavior, and demographic signals ensure your ads reach qualified prospects." },
  { icon: "ri-funds-line", title: "Performance-Based Optimization", desc: "Budget automatically shifts to the best-performing channels and audiences. No wasted spend on underperforming placements. ROI tracked and reported weekly." },
  { icon: "ri-image-line", title: "Professional Creative Assets", desc: "Drone aerials, 3D renders, 360-degree walkthroughs, and cinema-grade property videos. Your land is presented in the best possible light — dramatically increasing inquiry rates." },
  { icon: "ri-smartphone-line", title: "Instant Inquiry Capture", desc: "WhatsApp click-to-chat, lead forms, and direct call buttons on every ad and landing page. Buyers can connect with one tap — no complex forms, no friction." },
  { icon: "ri-filter-3-line", title: "Pre-Qualified Leads Only", desc: "Our team qualifies every lead before forwarding it to you. Budget check, intent verification, timeline assessment. You only speak to serious, ready-to-transact buyers." },
  { icon: "ri-dashboard-line", title: "Live Performance Dashboard", desc: "Real-time metrics on impressions, clicks, inquiries, cost-per-lead, and conversion rate. Monthly performance report with strategic recommendations for campaign optimization." },
];

const faqs = [
  { q: "How is your digital marketing different from listing on property portals?", a: "Property portals are passive — you list and hope someone finds you. Our approach is active and targeted. We use intent-based audience segmentation, Google Ads, Meta campaigns, and programmatic display to reach buyers who are actively searching for land in your area. We do not wait for buyers to find you — we put your property in front of the right buyers at the right moment." },
  { q: "What is the typical cost and ROI of a property marketing campaign?", a: "Campaign budgets typically range from ₹25,000 to ₹1,50,000 per month depending on property value and target audience size. Our average client achieves 8–12 qualified buyer inquiries per ₹25,000 spent, with a cost-per-qualified-lead of ₹2,000–3,000. For a property worth ₹1–5 Cr, this represents a marketing cost of under 1% of transaction value — with dramatically faster closure timelines." },
  { q: "Do I need to provide photographs and videos?", a: "No. We handle all content creation. Our team conducts a professional property shoot — drone aerials, 360-degree walkthroughs, and high-resolution stills. We also create 3D renderings of development potential to help buyers visualize what is possible on your land. All creative assets are included in the campaign setup." },
  { q: "How do I know the campaign is working?", a: "You receive a live dashboard with real-time metrics: impressions, clicks, landing page visits, inquiry form submissions, call tracking, and cost-per-lead. We also provide a monthly performance report with optimization insights. Every inquiry is qualified by our team before being forwarded to you — so you only speak to serious buyers." },
];

export default function DigitalMarketingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  usePageMeta({
    title: "Digital Marketing for Real Estate — Performance Campaigns That Sell Land | CashMyProperty.ai",
    description: "Sell your property faster with performance digital marketing. Google Ads, Meta campaigns, WhatsApp marketing, and programmatic display targeting active property buyers. Professional creative production included — drone aerials, 3D renders, and landing pages. Pre-qualified leads only. Live performance dashboard.",
    canonicalUrl: "https://cashmyproperty.ai/services/digital-marketing",
  });

  return (
    <PageLayout>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Hero */}
      <section className="relative w-full min-h-[480px] md:min-h-[520px] overflow-hidden bg-background-50">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20digital%20marketing%20analytics%20dashboard%20on%20large%20screen%20showing%20real%20estate%20campaign%20metrics%2C%20warm%20amber%20office%20lighting%2C%20professional%20marketer%20analyzing%20data%2C%20clean%20modern%20workspace%2C%20India%20real%20estate%20digital%20marketing%2C%20cinematic%20photography%2C%20deep%20teal%20and%20gold%20palette%2C%20high%20detail%2C%20data-driven%20aesthetic&width=1800&height=800&seq=service-digital-marketing-hero&orientation=landscape"
          alt="Digital Marketing — CashMyProperty.ai"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/25 to-background-50/40" />
        <div className="relative h-full flex items-center container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/10 mb-5">
              <span className="text-white text-xs font-medium uppercase tracking-widest">Service Detail</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold leading-[1.1] mb-4">
              <span className="text-white [text-shadow:0_0_24px_rgba(0,0,0,0.92)]">Digital</span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-primary-300 bg-clip-text text-transparent drop-shadow-[0_0_16px_rgba(0,0,0,0.78)]">Marketing</span>
            </h1>
            <p className="text-lg text-white/90 leading-relaxed max-w-lg [text-shadow:0_0_16px_rgba(0,0,0,0.84)]">
              Performance campaigns that put your property in front of the right buyers — and bring qualified leads directly to you.
            </p>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="py-4 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">
              <i className="ri-home-4-line mr-1" />Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <Link to="/services" className="hover:text-primary-500 transition-colors whitespace-nowrap">Services</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-950 font-medium">Digital Marketing</span>
          </nav>
        </div>
      </section>

      {/* What is Digital Marketing */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div>
              <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Property Marketing Reimagined</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
                The Right Buyer, At The Right Moment, On The Right Platform
              </h2>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Posting on property portals and waiting is not marketing — it is hoping. In today's digital landscape, serious buyers are actively searching on Google, scrolling Instagram, watching YouTube property tours, and responding to WhatsApp messages. If your property is not there when they are looking, someone else's is.
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                Our performance marketing engine uses <strong className="text-foreground-950">intent-based targeting</strong> across Google, Meta, YouTube, LinkedIn, and WhatsApp to reach buyers actively searching for land in your location. We do not spray ads at everyone — we surgically target the right buyer profiles with the right message at the right time.
              </p>
              <p className="text-foreground-600 leading-relaxed">
                Every lead is <strong className="text-foreground-950">qualified by our team</strong> before it reaches you. You speak only to serious buyers with verified budgets and genuine intent — saving you countless hours of unproductive conversations.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://readdy.ai/api/search-image?query=Social%20media%20marketing%20dashboard%20showing%20real%20estate%20campaign%20analytics%20with%20charts%20and%20engagement%20metrics%2C%20laptop%20and%20smartphone%20screens%2C%20warm%20amber%20lighting%2C%20clean%20modern%20workspace%2C%20India%20digital%20marketing%20agency%2C%20high%20detail%20photography%2C%20teal%20and%20gold%20tones%2C%20data%20visualization%20aesthetic&width=800&height=600&seq=digital-marketing-dashboard-01&orientation=landscape"
                alt="Digital Marketing Dashboard"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Channels */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Our Channels</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Multi-Channel Performance Marketing</h2>
            <p className="text-foreground-600">Your property, everywhere your buyers are looking.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {channels.map((ch) => (
              <div key={ch.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${ch.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{ch.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{ch.desc}</p>
                <span className="inline-block text-[10px] font-semibold px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 uppercase tracking-wider">{ch.reach}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Process</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">From Strategy to Sold — In 5 Steps</h2>
            <p className="text-foreground-600">A complete performance marketing funnel, managed end-to-end.</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.step} className="flex gap-6 pb-10 relative">
                {i < steps.length - 1 && <div className="absolute left-[26px] top-14 bottom-0 w-px bg-primary-900/20" />}
                <div className="w-[52px] h-[52px] flex items-center justify-center rounded-full bg-primary-500/10 border-2 border-primary-500/30 shrink-0 z-10">
                  <span className="text-sm font-display font-bold text-primary-500">{step.step}</span>
                </div>
                <div className="pt-2">
                  <div className="flex items-center gap-3 mb-2">
                    <i className={`${step.icon} text-xl text-primary-500`} />
                    <h3 className="font-display font-semibold text-lg text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">Benefits</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">Why Performance Marketing Wins</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((b) => (
              <div key={b.title} className="p-6 rounded-2xl card-dark hover:border-primary-500/40 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mb-4">
                  <i className={`${b.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-display font-semibold text-foreground-950 mb-2">{b.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-background-200/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { value: "₹2K–3K", label: "Cost Per Qualified Lead" },
              { value: "8–12", label: "Leads Per ₹25K Spent" },
              { value: "&lt;1%", label: "Marketing Cost vs Property Value" },
              { value: "300+", label: "Campaigns Delivered" },
            ].map((stat) => (
              <div key={stat.label} className="p-5">
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950 mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-foreground-950">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className={`rounded-xl border transition-all duration-300 ${openFaq === i ? "bg-background-200 border-primary-700/40" : "card-dark"}`}>
                <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-display font-semibold text-foreground-950 pr-4">{faq.q}</span>
                  <i className={`ri-add-line text-primary-500 text-xl shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-6 text-sm text-foreground-600 leading-relaxed">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Ready to Put Your Property in Front of Serious Buyers?
          </h2>
          <p className="text-foreground-600 mb-8">
            Share your property details. We will prepare a free campaign strategy with audience insights and estimated lead volume.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Free Campaign Strategy
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Get AI Report — ₹4,999
              <i className="ri-file-chart-line" />
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}